interface TestResult {
  score: number;
  tests: {
    category: string;
    grade_level: string;
    difficulty: string;
  };
}

interface PerformanceMetrics {
  recentScores: number[];
  strengthAreas: string[];
  weakAreas: string[];
  averageScore: number;
  suggestions: string[];
}

export const calculateDifficultyLevel = (recentScores: number[]): 'easy' | 'medium' | 'hard' => {
  if (!recentScores.length) return 'easy';
  
  const averageScore = recentScores.reduce((a, b) => a + b, 0) / recentScores.length;
  
  if (averageScore >= 85) return 'hard';
  if (averageScore >= 70) return 'medium';
  return 'easy';
};

const generateCategorySpecificRecommendations = (category: string, score: number, recentScores: number[]): string[] => {
  const recommendations: string[] = [];
  const isImproving = recentScores.length >= 2 && 
    recentScores[recentScores.length - 1] > recentScores[recentScores.length - 2];

  switch (category) {
    case 'Mathematics':
      if (score < 70) {
        recommendations.push(
          "Schedule 20-minute daily math practice sessions focusing on basic operations. Use physical objects like coins or blocks to make concepts more tangible.",
          "Break down word problems into smaller parts: first identify known values, then the question being asked, and finally plan the steps to solve it.",
          "Create a math journal where your child can write down problem-solving strategies and track improvements. This helps build confidence and identifies areas needing more practice."
        );
      } else if (score < 85) {
        recommendations.push(
          "Introduce real-world math challenges like calculating grocery bills or measuring ingredients while cooking together.",
          "Practice mental math during daily activities - for example, calculating discounts while shopping or estimating travel time based on distance.",
          "Create weekly math challenges that combine multiple concepts, starting with easier problems and gradually increasing difficulty."
        );
      }
      break;

    case 'Reading Comprehension':
      if (score < 70) {
        recommendations.push(
          "Set aside 30 minutes each day for paired reading: take turns reading paragraphs aloud and discuss unfamiliar words immediately.",
          "Create a 'story map' for each reading session: identify main characters, setting, problem, and solution. This helps build comprehensive understanding.",
          "Start a vocabulary journal with new words from readings. Practice using these words in daily conversations to reinforce learning."
        );
      } else if (score < 85) {
        recommendations.push(
          "Encourage prediction-making before reading new chapters: what might happen next and why? This builds critical thinking skills.",
          "Practice summarizing chapters in their own words and discussing different characters' motivations. This develops deeper comprehension.",
          "Create connections between the story and your child's experiences to make reading more relatable and memorable."
        );
      }
      break;

    case 'Science':
      if (score < 70) {
        recommendations.push(
          "Do simple science experiments at home using everyday materials. This makes abstract concepts more concrete and memorable.",
          "Watch educational science videos together and pause to discuss key concepts. Create a science vocabulary list from these sessions.",
          "Start a 'science journal' to record observations from daily life, like weather changes or plant growth."
        );
      } else if (score < 85) {
        recommendations.push(
          "Encourage hands-on exploration through structured experiments. Document hypotheses, procedures, and results.",
          "Connect science concepts to everyday life: discuss forces while playing sports, or chemical changes while cooking.",
          "Visit science museums or nature centers to reinforce learning through interactive exhibits and real-world examples."
        );
      }
      break;
  }

  // Add positive reinforcement for improvement
  if (isImproving) {
    recommendations.push(
      `Great progress in ${category}! Keep up the consistent practice - it's clearly making a difference.`
    );
  }

  // Add general study habit recommendations if score is low
  if (score < 70) {
    recommendations.push(
      "Create a dedicated study space free from distractions to improve focus and learning retention.",
      "Break study sessions into 25-minute blocks with 5-minute breaks to maintain attention and prevent fatigue."
    );
  }

  return recommendations;
};

export const analyzePerformance = async (childId: string, testResults: TestResult[] | undefined): Promise<PerformanceMetrics> => {
  if (!testResults || testResults.length === 0) {
    return {
      recentScores: [],
      strengthAreas: [],
      weakAreas: [],
      averageScore: 0,
      suggestions: [
        "Start with foundational concepts in each subject area.",
        "Set up a regular study schedule with dedicated time for each subject.",
        "Use interactive learning tools and educational games to make learning engaging."
      ]
    };
  }

  const recentScores = testResults.map(result => result.score);
  const averageScore = recentScores.reduce((a, b) => a + b, 0) / recentScores.length;

  const categoryScores: Record<string, { total: number; count: number; scores: number[] }> = {};
  
  testResults.forEach(result => {
    const category = result.tests.category;
    if (!categoryScores[category]) {
      categoryScores[category] = { total: 0, count: 0, scores: [] };
    }
    categoryScores[category].total += result.score;
    categoryScores[category].count += 1;
    categoryScores[category].scores.push(result.score);
  });

  const strengthAreas: string[] = [];
  const weakAreas: string[] = [];
  let suggestions: string[] = [];

  Object.entries(categoryScores).forEach(([category, { total, count, scores }]) => {
    const avgScore = total / count;
    const recentScore = scores[scores.length - 1];
    
    if (avgScore >= 85) {
      strengthAreas.push(category);
    } else if (avgScore <= 70) {
      weakAreas.push(category);
    }

    // Get 2-3 specific recommendations for each category
    const categoryRecommendations = generateCategorySpecificRecommendations(category, recentScore, scores);
    suggestions = [...suggestions, ...categoryRecommendations.slice(0, 3)];
  });

  return {
    recentScores,
    strengthAreas,
    weakAreas,
    averageScore,
    suggestions: [...new Set(suggestions)].slice(0, 5) // Limit to top 5 most relevant recommendations
  };
};

export const getRecommendations = (metrics: PerformanceMetrics): string[] => {
  return metrics.suggestions;
};